import { List, ListItemButton, ListItemText, Typography } from "@mui/material";
import { Box } from "@mui/system";
import BottomBar from "components/BottomBar";
import Loader from "components/Loader";
import { snack } from "components/toast";
import useQueryParams from "hooks/useQueryParams";
import _ from "lodash";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { ResType } from "types";
import Permissions from "views/settings/manage-users/roles-permissions/Permissions";
import { getClientPermissions, getPermissionClient, updateClientPermissions } from "api/services/clients/permissions";
import useTitle from "hooks/useTitle";
import { useClientData } from "context/ClientData";

function ViewProfile() {
  const params: any = useParams();
  const [originalState, setOriginalState] = useState<number[]>([]);
  const [permissions, setPermissons] = useState<number[]>([]);
  const { queryParams, setQueryParams } = useQueryParams();
  const queryClient = useQueryClient();
  const { data: clientData } = useClientData();
  useTitle(clientData?.data?.type === "CLIENT_GROUP" ? "Clients Group" : "Client");

  const { data: permissionsData, isLoading: permissionsLoading }: ResType = useQuery(
    "client-permissions",
    getClientPermissions
  );

  const parentGrouped = _.groupBy(permissionsData?.data, "parentLabel");
  const active = queryParams.tab || Object.keys(parentGrouped)[0];
  const labelGrouped = _.groupBy(parentGrouped[active], "label");

  const { data, isLoading }: ResType = useQuery(["permission-clients", params.clientId], getPermissionClient, {
    onSuccess: (res: any) => {
      let result = res.data?.permissions?.map((item: any) => item?.id) || [];
      setOriginalState(result);
      setPermissons(result);
    },
  });

  const { mutateAsync, isLoading: updateLoad } = useMutation(updateClientPermissions, {
    onSuccess: () => {
      queryClient.invalidateQueries("client-permissions");
      snack.success("Permissions Updated");
    },
    onError: (err: any) => {
      snack.error(err.response.data.message);
    },
  });

  const handlePermissionChange = (id: number) => {
    let filtered: number[] = [...permissions];
    if (permissions.includes(id)) {
      filtered = permissions.filter((item: any) => item !== id);
    } else {
      filtered.push(id);
    }
    setPermissons(filtered);
  };

  const handleUpdatePermissons = async () => {
    await mutateAsync({
      id: params.clientId,
      data: { permissions },
    });
  };

  if (permissionsLoading) return <Loader />;

  return (
    <>
      <Box p={2} pb={12}>
        <Typography sx={{ mb: 1 }} variant="subtitle1">
          Client Portal Permissions
        </Typography>
        <Box display="flex" gap={2}>
          <Box>
            <List
              sx={{
                width: 200,
                borderRight: "1px solid #e0e0e0",
                position: "sticky",
                top: 180,
                py: 0,
              }}
            >
              {Object.keys(parentGrouped)?.map((item: any) => (
                <ListItemButton
                  onClick={() => setQueryParams({ tab: item })}
                  selected={active === item}
                  sx={{
                    ...(active === item && {
                      borderRight: "3px solid red",
                      borderRadius: "2px",
                    }),
                  }}
                >
                  <ListItemText primary={<Typography variant="body2">{item}</Typography>} />
                </ListItemButton>
              ))}
            </List>
          </Box>
          <Box flex={1}>
            {Object.keys(labelGrouped)?.map((key: any) => (
              <Permissions
                label={key}
                data={labelGrouped[key]}
                permissions={permissions}
                handlePermissionChange={handlePermissionChange}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <BottomBar
        data={originalState}
        isLoading={updateLoad}
        state={permissions}
        onCancel={() => setPermissons(originalState)}
        onUpdate={handleUpdatePermissons}
      />
    </>
  );
}

export default ViewProfile;
